import { useState } from 'react';

import { Loader } from '@/components/Loader';
import { Switch } from '@/components/ui/switch';
import { useAuth } from '@/state/auth';

import { GlobalDisableConfirm } from './GlobalDisableConfirm';
import type { OperationsPlugin } from './hooks/useAllDiscoveryPlugins';
import { useAllDiscoveryPlugins } from './hooks/useAllDiscoveryPlugins';
import { usePluginCredits } from './hooks/usePluginCredits';
import { useToggleGlobalEnabled } from './hooks/useToggleGlobalEnabled';
import { useTogglePluginEnabled } from './hooks/useTogglePluginEnabled';
import { PluginConfigDialog } from './PluginConfigDialog';
import { PluginStatusCard } from './PluginStatusCard';

type ConfirmState =
  | { type: 'plugin'; plugin: OperationsPlugin }
  | { type: 'global' }
  | null;

export function DiscoveryPluginsPanel() {
  const {
    data: { paid, free },
    isGloballyEnabled,
    status,
  } = useAllDiscoveryPlugins();

  const { isPraetorianUser } = useAuth();
  const { data: creditsMap } = usePluginCredits(status === 'success');
  const [selectedPlugin, setSelectedPlugin] = useState<OperationsPlugin | null>(
    null
  );
  const [confirmDisable, setConfirmDisable] = useState<ConfirmState>(null);

  const { mutate: togglePlugin, isPending: isPluginTogglePending } =
    useTogglePluginEnabled();
  const { mutate: toggleGlobal, isPending: isGlobalTogglePending } =
    useToggleGlobalEnabled();

  const handleTogglePlugin = (plugin: OperationsPlugin, enabled: boolean) => {
    if (!enabled) {
      // Show confirmation before disabling
      setConfirmDisable({ type: 'plugin', plugin });
    } else {
      // Enable immediately — no confirmation needed
      if (plugin.piusId) {
        togglePlugin({ plugin: plugin.piusId, enabled: true });
      }
    }
  };

  const handleToggleGlobal = (enabled: boolean) => {
    if (!enabled) {
      // Show confirmation before disabling globally
      setConfirmDisable({ type: 'global' });
    } else {
      // Re-enable immediately — no confirmation needed
      toggleGlobal({ enabled: true });
    }
  };

  const handleConfirm = () => {
    if (!confirmDisable) return;

    if (confirmDisable.type === 'global') {
      toggleGlobal({ enabled: false });
    } else {
      const { plugin } = confirmDisable;
      if (plugin.piusId) {
        togglePlugin({ plugin: plugin.piusId, enabled: false });
      }
    }
    setConfirmDisable(null);
  };

  const confirmPluginName =
    confirmDisable?.type === 'plugin'
      ? confirmDisable.plugin.name
      : 'All Discovery Plugins';

  if (status === 'pending') {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader />
      </div>
    );
  }

  if (status === 'error') {
    return (
      <p className="text-chariot-text-secondary py-8 text-center text-sm">
        Failed to load discovery plugins. Please try again.
      </p>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      {/* Panel header with master kill switch (Praetorian users only) */}
      {isPraetorianUser && (
        <div className="flex items-center justify-between">
          <span className="text-chariot-text-secondary text-xs font-medium">
            Discovery
          </span>
          <div className="flex items-center gap-2">
            <span className="text-chariot-text-secondary text-xs">
              {isGloballyEnabled ? 'Enabled' : 'Disabled'}
            </span>
            <Switch
              checked={isGloballyEnabled}
              onCheckedChange={handleToggleGlobal}
              disabled={isGlobalTogglePending}
              aria-label={
                isGloballyEnabled
                  ? 'Disable all discovery plugins globally'
                  : 'Enable all discovery plugins globally'
              }
            />
          </div>
        </div>
      )}

      {/* Paid Enrichment Sources */}
      <section>
        <div className="mb-3">
          <h2 className="text-chariot-text-primary text-sm font-semibold">
            Paid Enrichment Sources
          </h2>
          <p className="text-chariot-text-secondary mt-1 text-xs">
            API-key plugins that require a master key for activation
          </p>
        </div>
        <div className="grid grid-cols-1 items-start gap-3 md:grid-cols-2 lg:grid-cols-3">
          {paid.map(plugin => (
            <PluginStatusCard
              key={plugin.id}
              plugin={plugin}
              onEdit={setSelectedPlugin}
              isPraetorianUser={isPraetorianUser}
              onToggleEnabled={handleTogglePlugin}
              isTogglePending={isPluginTogglePending}
              credits={
                plugin.piusId ? creditsMap?.get(plugin.piusId) : undefined
              }
            />
          ))}
        </div>
      </section>

      {/* Free Discovery Sources */}
      <section>
        <div className="mb-3">
          <h2 className="text-chariot-text-primary text-sm font-semibold">
            Free Discovery Sources
          </h2>
          <p className="text-chariot-text-secondary mt-1 text-xs">
            Built-in discovery plugins that run without API keys
          </p>
        </div>
        <div className="grid grid-cols-1 items-start gap-3 md:grid-cols-2 lg:grid-cols-3">
          {free.map(plugin => (
            <PluginStatusCard key={plugin.id} plugin={plugin} />
          ))}
        </div>
      </section>

      <PluginConfigDialog
        plugin={selectedPlugin}
        onOpenChange={open => !open && setSelectedPlugin(null)}
      />

      <GlobalDisableConfirm
        pluginName={confirmPluginName}
        open={confirmDisable !== null}
        onOpenChange={open => !open && setConfirmDisable(null)}
        onConfirm={handleConfirm}
        isPending={
          confirmDisable?.type === 'global'
            ? isGlobalTogglePending
            : isPluginTogglePending
        }
      />
    </div>
  );
}
